/**
 * ChangelogTimeline — activity feed rows.
 *
 * Renders plain-English summaries (via buildSummary), action icon+chip
 * (via classifyAction), collapsible old→new diffs (stacked, not strikethrough),
 * entity chips (clickable), scope chips for non-entity rows, relative time.
 *
 * Used both on the global History page (with showEntityName) and per-entity
 * EntityDetail (showEntityName=false).
 *
 * showSystem=false (default): system rows are hidden.
 * showSystem=true: system rows shown with grey "System" badge.
 */
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Pencil, Trash2, RefreshCw, ArrowRightLeft, Download,
  Building2, Cpu, CheckCircle2, ChevronDown, ChevronRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import type { ChangeLog } from "../lib/types";
import {
  formatChangelogValue,
  classifyAction,
  getActionVerb,
  isSystemRow,
  collapseSystemRuns,
  isCollapsedSystemRun,
} from "../lib/changelogFormat";
import { getFieldLabel } from "../lib/types";
import { cn } from "../lib/cn";

// Icon map for classifyAction icon names
const ICON_MAP: Record<string, React.ElementType> = {
  Plus: Plus,
  Pencil: Pencil,
  Trash2: Trash2,
  RefreshCw: RefreshCw,
  ArrowRightLeft: ArrowRightLeft,
  Download: Download,
  Building2: Building2,
  Cpu: Cpu,
  CheckCircle2: CheckCircle2,
};

interface Props {
  changes: ChangeLog[];
  showEntityName?: boolean;
  showSystem?: boolean;
}

function relativeTime(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso || "—";
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60_000);
    if (mins < 1) return i18n.t("audit:relative_just_now", { defaultValue: "just now" });
    if (mins < 60) return i18n.t("audit:relative_min_ago", { count: mins, defaultValue: `${mins} min ago` });
    const hours = Math.round(mins / 60);
    if (hours < 24) return i18n.t("audit:relative_hours_ago", { count: hours, defaultValue: `${hours}h ago` });
    const days = Math.round(hours / 24);
    if (days < 30) return i18n.t("audit:relative_days_ago", { count: days, defaultValue: `${days}d ago` });
    return d.toLocaleDateString(i18n.language || navigator.language);
  } catch {
    return iso || "—";
  }
}

/** A single timeline row for one ChangeLog item. */
function TimelineRow({
  item,
  showEntityName,
  showSystem,
}: {
  item: ChangeLog;
  showEntityName: boolean;
  showSystem: boolean;
}): JSX.Element | null {
  const { t } = useTranslation("audit");

  const isSystem = isSystemRow(item);
  if (isSystem && !showSystem) return null;

  const actionMeta = classifyAction(item.field);
  const IconComp = ICON_MAP[actionMeta.icon] ?? Pencil;

  const isLegacy = item.userId == null && (item.user == null || item.user.name == null);
  const actor = item.user?.name ?? item.user?.email ?? t("attributed_to_legacy_operator", { defaultValue: "Operator" });

  const fieldLabel = getFieldLabel(item.field);
  const verb = getActionVerb(item.field);

  // Entity info
  const entityId = item.entity?.id ?? (item.targetType === "entity" ? item.entityId : null);
  const entityName = item.entity?.name ?? item.targetName ?? "";

  // Diff line logic
  const TRUNCATE = 60;
  const truncate = (s: string) => s.length > TRUNCATE ? s.slice(0, TRUNCATE) + "…" : s;

  const actionType = actionMeta.type;
  const isCreatedOrAdded = actionType === "created" || actionType === "created-entity";
  const isDeletedOrRemoved = actionType === "deleted";

  // For created/added: show only new value; for deleted/removed: show only old value; else show old → new
  const showOldOnly = isDeletedOrRemoved;
  const showNewOnly = isCreatedOrAdded;

  // Format old/new for inline diff
  const rawOld = item.oldValue;
  const rawNew = item.newValue;
  const fmtOld = rawOld != null && rawOld !== "" ? formatChangelogValue(item.field, rawOld) : null;
  const fmtNew = rawNew != null && rawNew !== "" ? formatChangelogValue(item.field, rawNew) : null;
  const hasDiff = fmtOld !== null || fmtNew !== null;

  // Comment text — hide auto-generated audit hints (these duplicate the diff line):
  //   - Any leading "[...]" bracket marker, with or without internal spaces
  //     (e.g. "[mark-renewed]", "[reference-sync]", "[schedule update recomputed nextDueDate]")
  //   - Backend audit descriptions like "Obligation 'X' updated: ..." / "Obligation 'X' schedule updated"
  // Real free-text comments typed by a user (e.g. "Re-checked with legal") are preserved.
  const AUTO_COMMENT_PATTERNS: RegExp[] = [
    /^\[[^\]]+\]/,                               // any [bracketed] prefix
    /^Obligation\s+'[^']+'\s+/i,                 // "Obligation 'Annual filing' ..." audit hint
  ];
  const isAutoComment = item.comment
    ? AUTO_COMMENT_PATTERNS.some((re) => re.test(item.comment!.trim()))
    : false;
  const cleanedComment = isAutoComment ? "" : (item.comment ?? "").trim();
  const hasComment = cleanedComment.length > 0;

  // Collapse identical old/new diffs (e.g. JSON-blob fields where both sides
  // get the same friendly summary). Show the value once, no arrow.
  const sameOnBothSides =
    fmtOld != null && fmtNew != null && fmtOld.display === fmtNew.display;

  // Verb/field dedup: if the field label already contains the verb, suppress the verb
  const verbAndFieldLabel = useMemo(() => {
    const v = verb;
    const f = fieldLabel;
    if (f && v && f.toLowerCase().includes(v.toLowerCase())) {
      return { verb: null, fieldLabel: f };
    }
    return { verb: v, fieldLabel: f };
  }, [verb, fieldLabel]);

  const rowContent = (
    <div className="group flex items-start gap-3.5 py-3 px-6 rounded-md hover:bg-surface-2 transition-colors w-full">
      {/* Action circle */}
      <span
        className={cn(
          "w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0 mt-0.5",
          actionMeta.circleClass,
        )}
        title={t(`action_${actionMeta.type}`, { defaultValue: actionMeta.type })}
        aria-label={t(`action_${actionMeta.type}`, { defaultValue: actionMeta.type })}
        data-action-type={actionMeta.type}
      >
        <IconComp className="w-3.5 h-3.5 text-white" strokeWidth={2} aria-hidden />
      </span>

      <div className="flex-1 min-w-0">
        {/* Top line: actor · legacy tag · verb · field · entity */}
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className={cn("text-[13.5px] font-semibold", isLegacy && "text-muted")}>
            {actor}
          </span>
          {isLegacy && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 text-[10.5px] rounded bg-surface-2 text-muted font-medium"
              title={t("legacy_tooltip", { defaultValue: "Edit made before user-attribution wiring" })}
              data-testid="legacy-tag"
            >
              {t("legacy_tag", { defaultValue: "legacy" })}
            </span>
          )}
          {verbAndFieldLabel.verb && <span className="text-[13px] text-muted">{verbAndFieldLabel.verb}</span>}
          <span className="text-[13px] font-medium">{verbAndFieldLabel.fieldLabel}</span>
          {showEntityName && entityName && (
            <>
              <span className="text-dim text-[12.5px]" aria-hidden>·</span>
              {/* Country flag + name (when joined from BE) — sits to the left of the
                  entity name so operators see "🇲🇺 Mauritius · HIGHTEKERS MUS LTD"
                  at a glance. */}
              {item.entity?.country && (item.entity.country.flag || item.entity.country.name) && (
                <span
                  className="inline-flex items-center gap-1 text-[12.5px] text-muted whitespace-nowrap"
                  data-testid="row-country"
                >
                  {item.entity.country.flag && (
                    <span aria-hidden className="text-[14px] leading-none">
                      {item.entity.country.flag}
                    </span>
                  )}
                  {item.entity.country.name && <span>{item.entity.country.name}</span>}
                  <span className="text-dim mx-0.5" aria-hidden>·</span>
                </span>
              )}
              <span className="entity-name text-[13px]" data-testid="row-entity-name">{entityName}</span>
            </>
          )}
        </div>

        {/* Inline diff line */}
        {hasDiff && (
          <div className="text-[12.5px] mt-1.5 font-mono leading-relaxed">
            {showNewOnly ? (
              fmtNew && (
                <span className="diff-new" title={fmtNew.fullValue ?? undefined}>
                  {truncate(fmtNew.display)}
                </span>
              )
            ) : showOldOnly ? (
              fmtOld && (
                <span className="diff-old" title={fmtOld.fullValue ?? undefined}>
                  {truncate(fmtOld.display)}
                </span>
              )
            ) : sameOnBothSides ? (
              fmtNew && (
                <span className="diff-new" title={fmtNew.fullValue ?? undefined}>
                  {truncate(fmtNew.display)}
                </span>
              )
            ) : (
              <>
                {fmtOld && (
                  <span className="diff-old" title={fmtOld.fullValue ?? undefined}>
                    {truncate(fmtOld.display)}
                  </span>
                )}
                {fmtOld && fmtNew && (
                  <span className="diff-arrow mx-1.5" aria-hidden>→</span>
                )}
                {fmtNew && (
                  <span className="diff-new" title={fmtNew.fullValue ?? undefined}>
                    {truncate(fmtNew.display)}
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* Comment */}
        {hasComment && (
          <div className="text-[11.5px] mt-1 text-muted italic">
            &ldquo;{cleanedComment}&rdquo;
          </div>
        )}
      </div>

      {/* Relative time */}
      <time
        dateTime={item.createdAt}
        title={new Date(item.createdAt).toLocaleString(i18n.language)}
        className="text-[12px] text-dim font-mono tabular-nums shrink-0 mt-1"
      >
        {relativeTime(item.createdAt)}
      </time>

      <ChevronRight className="opacity-0 group-hover:opacity-100 w-4 h-4 text-dim mt-1 shrink-0 transition-opacity" aria-hidden />
    </div>
  );

  return (
    <li data-testid="timeline-row" className="list-none">
      {entityId ? (
        <Link
          to={`/entities/${entityId}?field=${encodeURIComponent(item.field)}`}
          className="block cursor-pointer no-underline text-inherit"
          aria-label={`${actor} ${verbAndFieldLabel.verb ?? ""} ${verbAndFieldLabel.fieldLabel} on ${entityName || "an entity"}`}
        >
          {rowContent}
        </Link>
      ) : (
        <div role="article" className="cursor-default">
          {rowContent}
        </div>
      )}
    </li>
  );
}

/** Collapsed system run summary. */
function CollapsedRunRow({ run, showSystem }: { run: { count: number; items: ChangeLog[] }; showSystem: boolean }): JSX.Element | null {
  const { t } = useTranslation("audit");
  const [expanded, setExpanded] = useState(false);

  if (!showSystem) return null;

  return (
    <li className="flex gap-3 py-2 px-[15px] bg-surface-2/40">
      <span aria-hidden className="w-7 h-7 shrink-0 rounded-full bg-surface-2 border border-border flex items-center justify-center text-dim mt-0.5">
        <Cpu className="h-[12px] w-[12px]" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <button
          type="button"
          className="text-[12.5px] text-muted hover:text-text flex items-center gap-1 min-h-[44px] sm:min-h-0"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />}
          {t("system_run_label", { count: run.count, defaultValue: `System migrated data for ${run.count} items` })}
        </button>
        {expanded && (
          <ul className="mt-1 pl-3 border-l-2 border-border-2 space-y-0 divide-y divide-border-2">
            {run.items.map((item) => (
              <TimelineRow key={item.id} item={item} showEntityName={false} showSystem />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

export function ChangelogTimeline({ changes, showEntityName = false, showSystem = false }: Props): JSX.Element {
  const { t } = useTranslation("audit");

  if (!changes.length) {
    return (
      <div className="text-center py-[18px] text-[13px] text-dim border border-dashed border-border rounded-[9px]">
        {t("empty")}
      </div>
    );
  }

  const timelineItems = collapseSystemRuns(changes);

  // Filter out system rows when showSystem=false (for the top-level list;
  // TimelineRow also does this guard, but we skip isCollapsedSystemRun entirely)
  const visibleItems = showSystem
    ? timelineItems
    : timelineItems.filter((item) =>
        isCollapsedSystemRun(item) ? false : !isSystemRow(item as ChangeLog)
      );

  if (visibleItems.length === 0) {
    return (
      <div className="text-center py-[18px] text-[13px] text-dim border border-dashed border-border rounded-[9px]">
        {t("empty")}
      </div>
    );
  }

  return (
    <ul className="px-3 space-y-0">
      {visibleItems.map((item, idx) => {
        if (isCollapsedSystemRun(item)) {
          return <CollapsedRunRow key={`run-${idx}`} run={item} showSystem={showSystem} />;
        }
        return (
          <TimelineRow
            key={(item as ChangeLog).id}
            item={item as ChangeLog}
            showEntityName={showEntityName}
            showSystem={showSystem}
          />
        );
      })}
    </ul>
  );
}
